import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { Observable, Subject, takeUntil } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { APP_INIT_STATES } from "../../store/status/status";
import { MessageType } from "../../ui/box/message-box/message.type";
import { ButtonType } from "../../ui/button/button.type";
import { BarcodeScanService } from "../../services/barcode-scan.service";
import { JoliooPharmacy } from "../../io/jolioo/voucher.types";
import { voucherErrorMessage } from "../../store/voucher/voucher-error";
import {
  checkVoucher,
  selectVoucherPharmacy,
} from "../../store/voucher/voucher.actions";
import {
  GetSelectedVoucherPharmacy,
  GetVoucherAvailabilityLoaded,
  GetVoucherCheckStatus,
  GetVoucherError,
  GetVoucherPharmacies,
} from "../../store/voucher/voucher.selector";

/** Schritt 1: Apotheke wählen und den Gutscheincode erfassen. */
@Component({
  selector: "app-voucher-scan",
  templateUrl: "./voucher-scan.component.html",
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VoucherScanComponent implements OnInit, OnDestroy {
  public readonly STATES = APP_INIT_STATES;
  public readonly MessageType = MessageType;
  public readonly ButtonType = ButtonType;

  public code = "";
  public codeInvalid = false;
  public scanning = false;
  public pharmacyValues: Array<{ id: string; text: string }> = [];

  public pharmacies$: Observable<Array<JoliooPharmacy>>;
  public checkStatus$: Observable<APP_INIT_STATES>;
  public availabilityLoaded$: Observable<boolean>;

  private _pharmacies: Array<JoliooPharmacy> = [];
  private _pharmacy: string | null = null;
  private _pharmacyKey = "";
  private _error: any = null;
  private _destroy = new Subject<void>();

  /** Gar keine zugeordnete Apotheke. */
  get hasPharmacy(): boolean {
    return this._pharmacies.length > 0;
  }

  /** Mehr als eine Apotheke — sonst zeigt das Dropdown sie nur als Text an. */
  /** Startwert fürs Dropdown, damit es nicht über eine bestehende Auswahl springt. */
  get selectedPharmacy(): string | null {
    return this._pharmacy;
  }

  get hasPharmacyChoice(): boolean {
    return this._pharmacies.length > 1;
  }

  /** Mehrere Apotheken, aber noch keine gewählt. */
  get needsPharmacyChoice(): boolean {
    return this.hasPharmacy && !this._pharmacy;
  }

  /**
   * Die gewählte Apotheke löst Gutscheine ein.
   * ⚠ Ohne `enabled` gilt sie als teilnehmend — ältere CMS-Stände lieferten
   * ausschliesslich freigeschaltete Apotheken.
   */
  get pharmacyParticipates(): boolean {
    const selected = this._pharmacies.find(
      (pharmacy) => pharmacy.id === this._pharmacy,
    );

    return !!selected && selected.enabled !== false;
  }

  /** Gewählt, aber nicht freigeschaltet — dann steht hier nur der Hinweis. */
  get pharmacyBlocked(): boolean {
    return !!this._pharmacy && !this.pharmacyParticipates;
  }

  /** Nichts zu wählen und nichts einzulösen: der Hinweis ersetzt die Seite. */
  get noRedeemablePharmacy(): boolean {
    return (
      !this.hasPharmacy || (!this.hasPharmacyChoice && this.pharmacyBlocked)
    );
  }

  get canCheck(): boolean {
    return !!this.code.trim() && this.pharmacyParticipates;
  }

  get checkError(): string {
    return voucherErrorMessage(
      this._error,
      "PAGES.VOUCHER.CHECK_ERROR",
      this._translate,
    );
  }

  constructor(
    private _store: Store,
    private _router: Router,
    private _route: ActivatedRoute,
    private _translate: TranslateService,
    private _barcodeScan: BarcodeScanService,
  ) {
    this.pharmacies$ = this._store.select(GetVoucherPharmacies);
    this.checkStatus$ = this._store.select(GetVoucherCheckStatus);
    this.availabilityLoaded$ = this._store.select(GetVoucherAvailabilityLoaded);
  }

  ngOnInit(): void {
    this.pharmacies$.pipe(takeUntil(this._destroy)).subscribe((pharmacies) => {
      // ⚠ Vor der Sperre: die Freischaltung kann sich ändern, ohne dass sich
      // die Liste ändert — das Dropdown bleibt davon unberührt.
      this._pharmacies = pharmacies;

      const key = pharmacies.map((pharmacy) => pharmacy.id).join("|");

      // ⚠ Nur bei echter Änderung neu aufbauen. Die Reducer klonen den State
      // (cloneDeep), der Selector liefert also bei jedem Dispatch ein neues
      // Array. Ein neues [values] löst ngOnChanges im Dropdown aus, das dabei
      // currentValue emittiert — und das würde hier wieder dispatchen.
      if (key === this._pharmacyKey) {
        return;
      }

      this._pharmacyKey = key;

      const values = pharmacies.map((pharmacy) => ({
        id: pharmacy.id,
        text: `${pharmacy.name} (${pharmacy.pharmacy_number})`,
      }));

      // ⚠ app-input-dropdown wählt in ngOnInit immer values[0]. Bei mehreren
      // Apotheken steht deshalb ein Platzhalter vorne — sonst wäre eine
      // Apotheke vorausgewählt und es würde versehentlich auf die falsche
      // gebucht. Die Auswahl muss bewusst getroffen werden.
      this.pharmacyValues =
        values.length > 1
          ? [
              {
                id: "",
                text: this._translate.instant(
                  "PAGES.VOUCHER.PHARMACY.PLACEHOLDER",
                ),
              },
              ...values,
            ]
          : values;
    });

    this._store
      .select(GetSelectedVoucherPharmacy)
      .pipe(takeUntil(this._destroy))
      .subscribe((pharmacy) => (this._pharmacy = pharmacy));

    this._store
      .select(GetVoucherError)
      .pipe(takeUntil(this._destroy))
      .subscribe((error) => (this._error = error));

    this.checkStatus$.pipe(takeUntil(this._destroy)).subscribe((status) => {
      if (status === APP_INIT_STATES.SUCCESS) {
        this._router.navigate(["betrag"], { relativeTo: this._route.parent });
      }
    });
  }

  ngOnDestroy(): void {
    this._destroy.next();
    this._destroy.complete();
  }

  /**
   * Übernimmt die Auswahl; der Platzhalter ist die Abwahl.
   *
   * ⚠ app-input-dropdown emittiert currentValue auch aus ngOnChanges heraus,
   * nicht nur bei echter Auswahl. Ohne die Sperre auf den unveränderten Wert
   * entsteht eine Endlosschleife (Dispatch -> neuer State -> neues [values] ->
   * ngOnChanges -> Dispatch …), die den Tab einfriert.
   */
  public onPharmacyChange(value: { id: string; text: string }): void {
    if (!value) {
      return;
    }

    const pharmacy = value.id || null;

    if (pharmacy === this._pharmacy) {
      return;
    }

    this._store.dispatch(selectVoucherPharmacy({ pharmacy }));
  }

  /** Nativ immer, im Browser nur mit Kamera-API (HTTPS oder localhost). */
  get scanAvailable(): boolean {
    return this._barcodeScan.isAvailable();
  }

  /**
   * Kamera oeffnen und den erkannten Code uebernehmen.
   *
   * ⚠ Nicht automatisch absenden: ein falsch erkannter Code ginge sonst
   * ungeprueft an Jolioo. Die Apotheker:in bestaetigt selbst.
   */
  public async startScan(): Promise<void> {
    this.scanning = true;

    try {
      const code = await this._barcodeScan.scan();

      if (code) {
        this.code = code.trim();
        this.codeInvalid = false;
      }
    } finally {
      this.scanning = false;
    }
  }

  public async cancelScan(): Promise<void> {
    await this._barcodeScan.cancel();
    this.scanning = false;
  }

  public onCodeChange(value: string): void {
    this.code = String(value ?? "");
    this.codeInvalid = false;
  }

  /** Reiner Lesezugriff — der Gutschein wird dabei nicht verändert. */
  public submitCode(): void {
    // ⚠ Der Fehlertext gehört zum Code — eine fehlende Apothekenauswahl ist
    // kein ungültiger Code und wird an der Auswahl selbst angezeigt.
    this.codeInvalid = !this.code.trim();

    if (this.codeInvalid || !this.pharmacyParticipates) {
      return;
    }

    this._store.dispatch(
      checkVoucher({
        code: this.code.trim(),
        pharmacy: this._pharmacy as string,
      }),
    );
  }
}
